import { buildChecklist, GROUP_LABELS, readinessSummary, STATUS_LABELS, type ItemGroup } from '../../readiness/rules.js';
import { nowIso, type ReadinessStatus } from '../../workspace/schema.js';
import { PageHead, Progress, StatusBadge } from '../components.js';
import { useWorkspace } from '../state.js';

const GROUPS: ItemGroup[] = ['request', 'financials', 'documents', 'owner'];

export function Readiness() {
  const { ws, update } = useWorkspace();
  const items = buildChecklist(ws);
  const sum = readinessSummary(items);

  const setOverride = (id: string, status: ReadinessStatus | null) => update((w) => {
    const checklist = { ...w.checklist };
    if (status === null) delete checklist[id];
    else checklist[id] = { status, updatedAt: nowIso() };
    return { ...w, checklist };
  });

  return (
    <>
      <PageHead eyebrow="Readiness" title="Your readiness checklist">
        What lenders commonly ask for, given your goal — what&rsquo;s done, what&rsquo;s left, and why each matters.
      </PageHead>
      <section className="card" style={{ marginBottom: 24 }}>
        <div className="spread">
          <div>
            <div className="big-number">{sum.ready} of {sum.total}</div>
            <div className="muted small">ready to discuss with a lender · {sum.needsReview} need review · {sum.notStarted} not started</div>
          </div>
          <a className="btn primary" href="#/plan">See your action plan</a>
        </div>
        <div style={{ marginTop: 14 }}><Progress value={sum.ready} max={sum.total} label="Checklist items ready" /></div>
      </section>

      <div className="stack-lg">
        {GROUPS.map((g) => {
          const list = items.filter((i) => i.group === g);
          if (list.length === 0) return null;
          return (
            <section key={g} className="card">
              <h2>{GROUP_LABELS[g]}</h2>
              {list.map((i) => (
                <div key={i.id} className={`ready-item ${i.status}`}>
                  <span className="ico" aria-hidden="true">{i.status === 'ready' ? '✓' : i.status === 'needs-review' ? '!' : ''}</span>
                  <div>
                    <strong>{i.title}</strong>{i.importance === 'helpful' && <span className="faint small"> · helpful</span>}
                    <p className="why">{i.why}</p>
                    {i.missing && <p className="missing">{i.missing}</p>}
                    <div className="row small">
                      {i.route && i.status !== 'ready' && <a href={i.route}>Work on this</a>}
                      {i.overridden
                        ? <button type="button" className="link-btn" onClick={() => setOverride(i.id, null)}>Undo my change</button>
                        : i.status !== 'ready' && (
                          <button type="button" className="link-btn" onClick={() => setOverride(i.id, 'ready')}>I have this ready</button>
                        )}
                    </div>
                  </div>
                  <div className="side">
                    <StatusBadge status={i.status} />
                    {i.overridden && <div className="faint small" style={{ marginTop: 4 }}>Marked by you</div>}
                  </div>
                </div>
              ))}
            </section>
          );
        })}
        <p className="small faint">Statuses: {Object.values(STATUS_LABELS).join(' · ')}. Lenders&rsquo; requirements vary; your action plan lists questions to confirm what yours needs.</p>
      </div>
    </>
  );
}
